import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatDateShort } from '@/lib/date';
import { downloadPublicFile } from '@/lib/fileDownload';
import { getStatusMeta, lessonStatusMap } from '@/lib/statusMap';
import StatusBadge from '@/components/base/StatusBadge';
import LessonEditModal, { type LessonRow, type GroupInfo } from './components/LessonEditModal';

export default function Dersler() {
  const [lessons, setLessons] = useState<LessonRow[]>([]);
  const [groupMap, setGroupMap] = useState<Record<string, GroupInfo>>({});
  const [docMap, setDocMap] = useState<Record<string, { title: string; file_url: string }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('hepsi');
  const [selectedLesson, setSelectedLesson] = useState<LessonRow | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: lessonsData, error: lessonsError }, { data: groupsData, error: groupsError }, { data: docsData, error: docsError }] =
        await Promise.all([
          supabase.from('zehrahoca_lessons').select('*').order('date', { ascending: false }),
          supabase.from('zehrahoca_groups').select('*').eq('active', true),
          supabase.from('zehrahoca_documents').select('id, lesson_id, title, file_url').not('file_url', 'is', null),
        ]);

      if (lessonsError) throw lessonsError;
      if (groupsError) throw groupsError;
      if (docsError) throw docsError;

      const map: Record<string, GroupInfo> = {};
      (groupsData ?? []).forEach((g: { id: string; name: string; color: string | null }) => {
        map[g.id] = { name: g.name, color: g.color ?? '#94a3b8' };
      });

      const docMapBuild: Record<string, { title: string; file_url: string }> = {};
      (docsData ?? []).forEach((d: { lesson_id: string | null; title: string; file_url: string | null }) => {
        if (d.lesson_id && d.file_url && !docMapBuild[d.lesson_id]) {
          docMapBuild[d.lesson_id] = { title: d.title, file_url: d.file_url };
        }
      });

      setLessons((lessonsData ?? []) as LessonRow[]);
      setGroupMap(map);
      setDocMap(docMapBuild);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const handleChange = () => fetchAll();
    window.addEventListener('zehrahoca:lessons-changed', handleChange);
    return () => {
      window.removeEventListener('zehrahoca:lessons-changed', handleChange);
    };
  }, [fetchAll]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { hepsi: lessons.length };
    lessons.forEach((l) => {
      counts[l.status] = (counts[l.status] ?? 0) + 1;
    });
    return counts;
  }, [lessons]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lessons.filter((l) => {
      const matchStatus = statusFilter === 'hepsi' || l.status === statusFilter;
      const matchSearch =
        !q ||
        `${l.title} ${l.subject ?? ''} ${groupMap[l.group_id ?? '']?.name ?? ''}`.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [lessons, search, statusFilter, groupMap]);

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Dersler yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Dersler yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button
            type="button"
            onClick={fetchAll}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Dersler</h1>
          <p className="mt-1 text-sm text-foreground-600">
            {lessons.length} ders · Tüm dersleri yönetin, düzenleyin ve canlı derslere katılın.
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event('zehrahoca:open-lesson-modal'))}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-add-line text-base" />
          Yeni Ders Ekle
        </button>
      </div>

      {/* Arama + durum filtreleri */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ders adı, konu veya grup ara..."
            className="w-full rounded-lg border border-background-300 bg-white py-2.5 pl-10 pr-3.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setStatusFilter('hepsi')}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition cursor-pointer ${
              statusFilter === 'hepsi'
                ? 'bg-primary-500 text-white'
                : 'bg-white text-foreground-600 ring-1 ring-background-300 hover:bg-background-100'
            }`}
          >
            Tümü ({statusCounts.hepsi ?? 0})
          </button>
          {Object.entries(lessonStatusMap).map(([value, meta]) => (
            <button
              key={value}
              type="button"
              onClick={() => setStatusFilter(value)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition cursor-pointer ${
                statusFilter === value
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-foreground-600 ring-1 ring-background-300 hover:bg-background-100'
              }`}
            >
              {meta.label} ({statusCounts[value] ?? 0})
            </button>
          ))}
        </div>
      </div>

      {/* Ders tablosu */}
      <div className="overflow-hidden rounded-xl border border-background-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/60 text-xs text-foreground-500">
                <th className="px-4 py-3 font-semibold">Ders</th>
                <th className="px-4 py-3 font-semibold">Grup</th>
                <th className="px-4 py-3 font-semibold">Tarih &amp; Saat</th>
                <th className="px-4 py-3 font-semibold">Durum</th>
                <th className="px-4 py-3 text-right font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-foreground-500">
                    Eşleşen ders bulunamadı. Farklı bir arama veya filtre deneyin.
                  </td>
                </tr>
              )}
              {filtered.map((l) => {
                const group = l.group_id ? groupMap[l.group_id] : undefined;
                return (
                  <tr key={l.id} className="border-b border-background-100 last:border-0 hover:bg-background-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: group?.color ?? '#94a3b8' }}
                        />
                        <div>
                          <p className="font-medium text-foreground-900">{l.title}</p>
                          {l.subject && <p className="mt-0.5 text-xs text-foreground-500">{l.subject}</p>}
                          {docMap[l.id] ? (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-600">
                              <i className="ri-file-pdf-2-line" /> PDF var
                            </span>
                          ) : (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-background-100 px-2 py-0.5 text-[11px] font-semibold text-foreground-400">
                              <i className="ri-file-line" /> PDF yok
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-foreground-700">
                      {group?.name ?? '-'}
                      {l.type === 'birebir' && (
                        <span className="ml-2 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-800">
                          Birebir
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground-600">
                      {l.date ? formatDateShort(l.date) : '—'}
                      {l.start_time && <span className="ml-1 text-foreground-400">· {l.start_time}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge meta={getStatusMeta(l.status, lessonStatusMap)} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {docMap[l.id] && (
                          <>
                            <a
                              href={docMap[l.id].file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="PDF Aç"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100 cursor-pointer"
                            >
                              <i className="ri-external-link-line text-base" />
                            </a>
                            <button
                              type="button"
                              onClick={() => downloadPublicFile(docMap[l.id].file_url, docMap[l.id].title)}
                              title="PDF İndir"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100 cursor-pointer"
                            >
                              <i className="ri-download-line text-base" />
                            </button>
                          </>
                        )}
                        {l.live_link && (
                          <a
                            href={l.live_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-600 cursor-pointer"
                          >
                            <i className="ri-vidicon-line" /> Canlı Ders
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => setSelectedLesson(l)}
                          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-white px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
                        >
                          <i className="ri-edit-line" /> Düzenle
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <LessonEditModal
        open={selectedLesson !== null}
        onClose={() => setSelectedLesson(null)}
        lesson={selectedLesson}
        groupMap={groupMap}
        onSaved={fetchAll}
        onDeleted={fetchAll}
      />
    </div>
  );
}